import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { earnPoints } from "@/lib/usePoints";
import PhotoGallery from "@/components/PhotoGallery";
import VideoGallery from "@/components/VideoGallery";
import ReviewForm from "@/components/ReviewForm";
import ReviewList from "@/components/ReviewList";
import { Stars, photoSrc } from "@/components/Stars";
import { likeHeaders, useLikeToggle } from "@/lib/useLikes";
import { storage } from "@/lib/storage";
import { useHiddenAuthors } from "@/lib/useHidden";

export { Stars, photoSrc };

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { reviews: [], stats: { avg: null, count: 0 }, mine: null };

export default function CountryReviews({ code, country }) {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const openId = searchParams.get("m");
  const countryName = country ? (country.short_names[lang] || country.short_names.en) : code;
  const [data, setData] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(EMPTY); setLoaded(false);
    fetch(`${API}/api/countries/${code}/reviews`, { credentials: "include", headers: likeHeaders() })
      .then((r) => r.json())
      .then((d) => { if (alive) { setData(d); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [code, user]);

  const submit = async ({ rating, text, name, email, photo_id = null, video_id = null }) => {
    storage.set("atlas.visitorName", name);
    storage.set("atlas.visitorEmail", email || "");
    const r = await fetch(`${API}/api/countries/${code}/reviews`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...likeHeaders() },
      body: JSON.stringify({ rating, text, lang, name, email: email || null, photo_id, video_id }),
    });
    if (!r.ok) throw new Error("review");
    const mine = await r.json();
    setData((d) => ({ ...d, mine }));
    trackEvent("submit_review", { country_code: code, rating });
    earnPoints("review", lang);
    return mine;
  };

  const { mine } = data;
  const { filter } = useHiddenAuthors();
  const reviews = useMemo(() => filter(data.reviews), [data.reviews, filter]);
  const stats = useMemo(() => {
    if (reviews.length === data.reviews.length) return data.stats;
    const count = reviews.length;
    return { count, avg: count ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / count) * 10) / 10 : null };
  }, [reviews, data.reviews.length, data.stats]);
  const toggleLike = useLikeToggle(setData, code);
  const formDefaults = useMemo(() => ({
    name: mine?.author?.name || user?.name || storage.get("atlas.visitorName") || "",
    email: user?.email || storage.get("atlas.visitorEmail") || "",
  }), [mine, user]);

  return (
    <div className="space-y-4" data-testid="country-reviews">
      <PhotoGallery reviews={reviews} code={code} onLike={toggleLike} openId={openId} countryName={countryName} />
      <VideoGallery reviews={reviews} code={code} onLike={toggleLike} openId={openId} countryName={countryName} />
      <div className="flex items-center gap-3" data-testid="review-stats">
        {stats.count > 0 ? (
          <>
            <span className="stat-number text-2xl text-ink">{stats.avg}</span>
            <Stars value={Math.round(stats.avg)} />
            <span className="text-xs text-muted-foreground">({stats.count})</span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">{t("review_none")}</span>
        )}
      </div>

      {loaded && (
        <ReviewForm key={`${code}-${mine?.id || "new"}`} mine={mine} onSubmit={submit} defaults={formDefaults} />
      )}

      <ReviewList reviews={reviews} />
    </div>
  );
}
